<template>
  <div class="content-block">
    <div class="app-buttons">
      <dx-button
        type="normal"
        :text="$t('logs.buttons.cleatfilter')"
        @click="onFilterClear"
      />
    </div>

    <dx-data-grid
      class="data-grid dx-card"
      :data-source="dataSourceConfig"
      :height="gridHeight"
      :remote-operations="remoteOperations"
      :syncLookupFilterValues="false"
      @content-ready="onContentReady"
      @row-click="onRowClick"
      @context-menu-preparing="addMenuItems"
      @editor-preparing="onEditorPreparing"
      @initialized=initializedGrid
      ref="dataGrid"
    >
      <dx-paging :enabled="true" />
      <dx-pager
        :visible="true"
        :allowed-page-sizes="pageSize"
        display-mode="full"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="false"
      />

      <dx-filter-row :visible="true" />
      <dx-header-filter :visible="true" />
      <dx-sorting mode="multiple" />
      <dx-state-storing
        :enabled="true"
        type="custom"
        :customLoad="stateLoad"
        :customSave="stateSave"
      />
      <dx-filter-panel :visible="showFilterPanel" />

      <dx-column
        data-field="logDatetime"
        :caption="$t('logs.date')"
        :width="146"
        data-type="datetime"
        format="dd.MM.yyyy HH:mm:ss"
        sort-order="desc"
        :allow-header-filtering="false"
        :editor-options="{ calendarOptions: { firstDayOfWeek: 1 } }"
      />

      <dx-column v-if="showGroup"
        data-field="eventGroup.id"
        :caption="$t('logs.group')"
        :width="175"
        :header-filter="{ dataSource: eventGroupDataSourceConfig }"
      >
        <dx-lookup
          :data-source="eventGroupDataSourceConfig"
          value-expr="id"
          display-expr="name"
        />
      </dx-column>

      <dx-column
        data-field="logLevel"
        :caption="$t('logs.recordtype')"
        :width="165"
        :header-filter="{ dataSource: logLevelDataSourceConfig }"
        cell-template="log-level-cell"
      >
        <dx-lookup
          :data-source="logLevelDataSourceConfig"
          value-expr="id"
          display-expr="text"
        />
      </dx-column>

      <dx-column
        data-field="user.id"
        :caption="$t('logs.user')"
        :width="140"
        :header-filter="{ dataSource: userDataSourceConfig }"
      >
        <dx-lookup
          :data-source="userDataSourceConfig"
          value-expr="id"
          display-expr="text"
        />
      </dx-column>

      <dx-column
        data-field="childrenCount"
        :caption="$t('logs.children')"
        :width="50"
        :allow-header-filtering="false"
      />

      <dx-column
        data-field="logDuration"
        :caption="$t('logs.time')"
        :width="90"
        data-type="number"
        format="#,##0.000"
        :allow-header-filtering="false"
      />

      <dx-column
        data-field="message"
        :caption="$t('logs.message')"
        :allow-header-filtering="false"
      />

      <dx-column
        data-field="objectClass"
        :visible="false"
      />

      <dx-column
        data-field="objectId"
        :visible="false"
        data-type="number"
      />

      <template #log-level-cell="{ data }">
        <div :style="{ color: formatLogKindColor(data.value) }">{{ data.displayValue }}</div>
      </template>
    </dx-data-grid>

    <CustomColumnChooser
      :visible="visible"
      :columns="columns"
      :onApply="onApply"
      :onHiding="onHiding"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { formatLogKindColor, logLevelDataSourceConfig } from "./constants";
import baseView from "../base";
import appInfo from "../../app-info";
import { eventGroupDataSourceConfig } from "./event-groups";

import DxDataGrid, {
  DxColumn, DxLookup, DxFilterRow, DxHeaderFilter, DxPaging, DxStateStoring, DxFilterPanel,
  DxPager, DxSorting
} from "devextreme-vue/data-grid";
import { DxButton } from 'devextreme-vue';

import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { findSystemLog } from "@/services/api";
import { getUserDataSource } from "@/services/cache";
import { AclHandler } from "@/services/acl-handler";

import { DataDrid } from "@/mixins/dataDridMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';

import _ from "underscore";

export default {
  extends: baseView,
  mixins: [DataDrid],
  components: {
    DxDataGrid, DxColumn, DxLookup, DxFilterRow, DxHeaderFilter, DxPaging, DxButton, DxStateStoring,
    DxFilterPanel, CustomColumnChooser, DxPager, DxSorting
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      remoteOperations: { groupPaging: true },
      dataSourceConfig: { store: this.createDataSource(findSystemLog) },
      logLevelDataSourceConfig,
      eventGroupDataSourceConfig,
      userDataSourceConfig: null,
      showGroup: true
    };
  },
  computed: {
    ...mapGetters(['getGridState']),
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    showFilterPanel() {
      return this.aclUser.hasGrantForOperation('BTN_CLEAR_FILTER', 'EXECUTE') || false;
    },
    sumGap() {
      return PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
    },
    gridHeight() {
      const appHeightSize = this.$store.getters.appContainerSize.height;
      return (appHeightSize || 800) - this.sumGap;
    },
    gridBodyHeight() {
      return this.gridHeight - GRID_HEADER_HEIGHT - GRID_FILTER_ROW_HEIGHT - (this.showFilterPanel ? GRID_FILTER_PANEL_HEIGHT : 0) - GRID_PAGER_HEIGHT;
    },
  },
  methods: {
    formatLogKindColor,
    onEditorPreparing(e) {
      this.applyFilterRowTimeout(e);
    },
    onContentReady(e) {
      this.addFitToPageHeightButton(e);
    },
    stateLoad() {
      let state = {...{}, ...this.getGridState(this.gridStateKey)};
      if (!state)
        state = {
          pageIndex: 0,
          pageSize: this.countRows(this.gridBodyHeight)
        };

      // console.debug('logs::stateLoad', state);
      return state;
    },
    stateSave(state) {
      return this.$store.commit("setGridsStates", { key: this.gridStateKey, state: state});
    },
    convertRecord(rec) {
      if (("logDuration" in rec) && !_.isNull(rec.logDuration)) {
        rec.logDuration = rec.logDuration / 1000;
      }
    },
    onRowClick(e) {
      if (window.getSelection().focusOffset !== window.getSelection().anchorOffset)
        return;

      if (e.event.metaKey || e.event.ctrlKey) {
        const routeData = this.$router.resolve({
          name: "log",
          params: { id: e.data.id }
        });
        window.open(routeData.href, "_blank");
      } else {
        this.$router.push({
          name: "log",
          params: { id: e.data.id }
        });
      }
    },
    updateBreadcrumbAndTitle() {
      document.title = `${appInfo.title} - ${this.$t('logs.title')}`;
      this.$store.commit("setBreadcrumb", [{
        link: "/logs",
        title: `${this.$t('logs.title')}`
      }]);
    }
  },
  async created() {
    this.gridStateKey = 'gridLogs';

    this.updateBreadcrumbAndTitle();
    this.userDataSourceConfig = await getUserDataSource();
  },
  beforeDestroy() {
    const state = this.dataGrid.state();
    this.stateSave(state);
  },
  watch: {
  }
};
</script>

<style lang="scss" scoped>
.app-buttons {
  justify-content: flex-start !important;
}
</style>
